/**
 * NATS debug injector — framework-agnostic controller.
 *
 * Injects a message into the SAME Redux path a real NATS message takes. Every
 * live/direct-get/snapshot message ultimately funnels through
 * `store.dispatch(updateMessage({ subject, payload }))` (see natsclient.ts),
 * which the spaces slice consumes via `processMessage(state, subject, payload)`.
 * Dispatching that action here reproduces a received message exactly — same
 * reducer, same store mutation, same selectors/re-renders — while bypassing only
 * the transport concerns that don't affect app state (websocket, JSON codec,
 * Nats-Sequence de-dup). It works for ANY subject the reducer understands
 * (e.g. `lh...`, `signageAnalytical...`, `events...`), not just location.
 *
 * This module is debug-only and self-contained: it never imports app UI and is
 * only pulled in when the debug panel is mounted. It also exposes a
 * `window.__natsinjector` console API mirroring the on-screen panel.
 */
import { store } from '../../../../store/store';
import { updateMessage, updateMessages } from '../../../../store/natsslice';
const MAX_HISTORY = 50;
function assertSubject(subject) {
    if (typeof subject !== 'string' || subject.trim() === '') {
        throw new Error('nats-debug: `subject` must be a non-empty string');
    }
}
class NatsDebugController {
    constructor() {
        this.open = false;
        this.listeners = new Set();
        this.historyItems = [];
        /** Subscribe to open/history changes. Returns an unsubscribe function. */
        this.subscribe = (listener) => {
            this.listeners.add(listener);
            return () => {
                this.listeners.delete(listener);
            };
        };
    }
    isOpen() {
        return this.open;
    }
    getHistory() {
        return this.historyItems;
    }
    emit() {
        this.listeners.forEach((listener) => listener());
    }
    setOpen(next) {
        if (this.open === next)
            return;
        this.open = next;
        this.emit();
    }
    toggle() {
        this.setOpen(!this.open);
    }
    /** Inject a single message: identical to one received NATS message. */
    inject(subject, payload) {
        assertSubject(subject);
        store.dispatch(updateMessage({ subject, payload }));
        this.record(subject, payload);
    }
    /** Inject many messages in one store notification (mirrors snapshot batch). */
    injectBatch(entries) {
        if (!Array.isArray(entries) || entries.length === 0) {
            throw new Error('nats-debug: `injectBatch` expects a non-empty array');
        }
        entries.forEach((entry) => assertSubject(entry === null || entry === void 0 ? void 0 : entry.subject));
        store.dispatch(updateMessages(entries));
        entries.forEach((entry) => this.record(entry.subject, entry.payload));
    }
    /** Current spaces slice — handy for cloning a real payload to tweak. */
    getSpacesState() {
        return store.getState().spacesData;
    }
    record(subject, payload) {
        this.historyItems = [
            { subject, payload, at: Date.now() },
            ...this.historyItems,
        ].slice(0, MAX_HISTORY);
        this.emit();
    }
}
export const natsDebugController = new NatsDebugController();
// Side-effect: expose the console API as soon as this module loads. The panel
// imports this module, so mounting the panel is enough to enable console use.
if (typeof window !== 'undefined') {
    window.__natsinjector = {
        inject: (subject, payload) => natsDebugController.inject(subject, payload),
        injectBatch: (entries) => natsDebugController.injectBatch(entries),
        open: () => natsDebugController.setOpen(true),
        close: () => natsDebugController.setOpen(false),
        toggle: () => natsDebugController.toggle(),
        getState: () => natsDebugController.getSpacesState(),
        history: () => natsDebugController.getHistory(),
    };
}
